import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';
import { Route, RouteApi, RoutesFilter } from '../models';
import { RouteMonitoring, RouteMonitoringApi } from '../models';
import { RouteTrajectory, RouteTrajectoryApi } from '../models';
import type {
  AssignDriverRequest,
  AssignDriverResponse,
  ConfirmRouteRequest,
  ConfirmRouteResponse,
  AddShippingsToRouteRequest,
  AddShippingsToRouteResponse,
  CancelRouteRequest,
  CancelRouteResponse,
  RouteCloneCandidates,
  RouteCloneCandidatesRequest,
} from '../models';
import {
  mapRouteApiToModel,
  mapRouteMonitoringApiToModel,
  mapRouteTrajectoryApiToModel,
} from '../adapters';
import {
  buildAssignDriverRequest,
  buildReassignDriverRequest,
  buildConfirmRouteRequest,
  buildAddShippingsToRouteRequest,
  buildCancelRouteRequest,
  buildRouteCloneCandidatesRequest,
} from '../utils/routeRequestBuilders';
import { normalizeRoutesScheduledFilterValue } from '../utils';

export interface GetRoutesParams {
  paginated: PaginatedRequest;
  filters?: RoutesFilter;
}

const routesService = createApi({
  reducerPath: 'routesService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/routes`),
  tagTypes: ['Routes', 'RouteDetail', 'RouteTrajectory'],
  endpoints: (builder) => ({
    getRoutes: builder.query<PaginatedResponse<Route>, GetRoutesParams>({
      query: ({ paginated, filters }) => {
        const params: Record<string, string> = {
          limit: String(paginated.pageSize),
          offset: String((paginated.page - 1) * paginated.pageSize),
        };

        const driverIds = (filters?.driverIds ?? [])
          .map((id) => id.trim())
          .filter((id) => id.length > 0);
        if (driverIds.length > 0) {
          params.driverIds = [...new Set(driverIds)].join(',');
        }

        const logisticOperatorId = filters?.logisticOperatorId?.trim();
        if (logisticOperatorId) {
          params.logisticOperatorId = logisticOperatorId;
        }

        const commerceId = filters?.commerceId?.trim();
        if (commerceId) {
          params.commerceId = commerceId;
        }

        if (filters?.routeName) {
          const routeName = filters.routeName.trim();
          if (routeName.length > 0) {
            params.routeName = routeName;
          }
        }

        const routeId = filters?.routeId?.trim();
        if (routeId) {
          params.routeId = routeId;
        }

        const scheduledFrom = filters?.scheduledFrom?.trim();
        if (scheduledFrom && !routeId) {
          params.scheduledFrom = normalizeRoutesScheduledFilterValue(
            scheduledFrom,
            'from'
          );
        }

        const scheduledTo = filters?.scheduledTo?.trim();
        if (scheduledTo && !routeId) {
          params.scheduledTo = normalizeRoutesScheduledFilterValue(
            scheduledTo,
            'to'
          );
        }

        if (filters?.status) {
          params.status = filters.status;
        }

        const shippingNumber = filters?.shippingNumber?.trim();
        if (shippingNumber) {
          params.shippingNumber = shippingNumber;
        }

        return {
          url: '',
          params,
        };
      },
      transformResponse: (response: PaginatedResponse<RouteApi>) => ({
        ...response,
        items: response.items.map(mapRouteApiToModel),
      }),
      providesTags: ['Routes'],
    }),
    getRouteById: builder.query<RouteMonitoring, { routeId: string }>({
      query: ({ routeId }) => ({
        url: `/${routeId}`,
      }),
      transformResponse: (response: RouteMonitoringApi): RouteMonitoring =>
        mapRouteMonitoringApiToModel(response),
      providesTags: (_result, _error, { routeId }) => [
        { type: 'RouteDetail' as const, id: routeId },
        'RouteDetail',
      ],
    }),
    getRouteTrajectory: builder.query<RouteTrajectory, { routeId: string }>({
      query: ({ routeId }) => ({
        url: `/${routeId}/trajectory`,
      }),
      transformResponse: (response: RouteTrajectoryApi): RouteTrajectory =>
        mapRouteTrajectoryApiToModel(response),
      providesTags: (_result, _error, { routeId }) => [
        { type: 'RouteTrajectory' as const, id: routeId },
        'RouteTrajectory',
      ],
    }),
    assignDriver: builder.mutation<AssignDriverResponse, AssignDriverRequest>({
      query: (params) => buildAssignDriverRequest(params),
      invalidatesTags: ['Routes', 'RouteDetail'],
    }),
    reassignDriver: builder.mutation<AssignDriverResponse, AssignDriverRequest>({
      query: (params) => buildReassignDriverRequest(params),
      invalidatesTags: ['Routes', 'RouteDetail'],
    }),
    confirmRoute: builder.mutation<ConfirmRouteResponse, ConfirmRouteRequest>({
      query: (params) => buildConfirmRouteRequest(params),
      invalidatesTags: ['Routes', 'RouteDetail'],
    }),
    addShippingsToRoute: builder.mutation<
      AddShippingsToRouteResponse,
      AddShippingsToRouteRequest
    >({
      query: (params) => buildAddShippingsToRouteRequest(params),
      invalidatesTags: ['Routes', 'RouteDetail'],
    }),
    cancelRoute: builder.mutation<CancelRouteResponse, CancelRouteRequest>({
      query: (params) => buildCancelRouteRequest(params),
      invalidatesTags: ['Routes', 'RouteDetail'],
    }),
    getRouteCloneCandidates: builder.query<
      RouteCloneCandidates,
      RouteCloneCandidatesRequest
    >({
      query: (params) => buildRouteCloneCandidatesRequest(params),
    }),
    initiateExport: builder.mutation<
      import('../models').InitiateExportResponse,
      RoutesFilter
    >({
      query: (filters) => {
        const body: Record<string, string | string[]> = {};

        const routeId = filters.routeId?.trim();
        if (routeId) {
          body.routeId = routeId;
        } else {
          if (filters.scheduledFrom) body.scheduledFrom = filters.scheduledFrom;
          if (filters.scheduledTo) body.scheduledTo = filters.scheduledTo;
        }
        if (filters.logisticOperatorId) body.logisticOperatorId = filters.logisticOperatorId;
        if (filters.commerceId) body.commerceId = filters.commerceId;
        if (filters.routeName) body.routeName = filters.routeName;
        if (filters.status) body.status = filters.status;

        const driverIds = (filters.driverIds ?? [])
          .map((id) => id.trim())
          .filter(Boolean);
        if (driverIds.length > 0) body.driverIds = [...new Set(driverIds)];

        return {
          url: '/export-excel',
          method: 'POST',
          body,
        };
      },
    }),
    getExportJobStatus: builder.query<import('../models').ExportJob, string>({
      query: (jobId) => ({
        url: `/export-excel/jobs/${jobId}`,
      }),
    }),
  }),
});

export default routesService;

export const {
  useGetRoutesQuery,
  useGetRouteByIdQuery,
  useGetRouteTrajectoryQuery,
  useAssignDriverMutation,
  useReassignDriverMutation,
  useConfirmRouteMutation,
  useAddShippingsToRouteMutation,
  useCancelRouteMutation,
  useLazyGetRouteCloneCandidatesQuery,
  useInitiateExportMutation,
  useLazyGetExportJobStatusQuery,
} = routesService;
